import React, { MouseEventHandler } from 'react';
import styled from 'styled-components';
import { Button, BodyText, Subtitle } from '@kvdbil/components';
import Head, { Props as HeadProps } from '~/App/shared/components/Head';
import ForgotPasswordForm from './components/Form';
import { TranslateFunction, useTranslation } from '~/Locale';
import Section from '../Layout/Section';

const StyledSection = styled(Section)`
  text-align: center;
  flex: 1;
  padding: 0 2rem;
`;

const StyledHeadline = styled(Subtitle)`
  padding-bottom: 1.5rem;
`;

const StyledBodyText = styled(BodyText)`
  padding-bottom: 1.5rem;
`;

const BackButtonBase = styled(Button)`
  margin-left: auto;
  margin-right: auto;
  margin-top: 0.25rem;
`;

const InvalidResetPasswordContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light3};
  margin-bottom: 1.5rem;
`;

interface BackButtonProps {
  handleClose?: Props['handleClose'];
  t: TranslateFunction;
}

const BackButton = ({ handleClose, t }: BackButtonProps) => (
  <BackButtonBase onClick={handleClose} variant="flat" color="secondary">
    {t('Go back')}
  </BackButtonBase>
);

export interface Props {
  isBiddingView?: boolean;
  isResetPassword?: boolean;
  handleClose?: MouseEventHandler<HTMLButtonElement>;
  data?: HeadProps;
  defaultEmail?: string;
}

const ForgotPassword = ({
  data,
  isBiddingView,
  isResetPassword,
  handleClose
}: Props) => {
  const { t } = useTranslation();
  const Container = isBiddingView ? React.Fragment : StyledSection;

  return (
    <>
      {!isBiddingView && (
        <Head {...data} title={t('PAGE_TITLES.FORGOT_PASSWORD')} />
      )}

      <Container
        {...(isBiddingView
          ? {}
          : { spacing: 0, backgroundColor: 'light', maxWidth: 22.5 })}
      >
        {isResetPassword && (
          <InvalidResetPasswordContainer>
            <StyledHeadline as="h2">
              {t('Oops, this is an old link!')}
            </StyledHeadline>
            <StyledBodyText>
              {t(
                'This may be because you either already used the link to reset your password or simply lost it.'
              )}
            </StyledBodyText>
          </InvalidResetPasswordContainer>
        )}
        <div>
          <StyledHeadline as="h3">{t('Forgot password?')}</StyledHeadline>
          <StyledBodyText>
            {t(
              'We will send you a link with instructions on how to reset your password.'
            )}
          </StyledBodyText>
          <ForgotPasswordForm />
          {isBiddingView && <BackButton {...{ t, handleClose }} />}
        </div>
      </Container>
    </>
  );
};

export default ForgotPassword;
